import { useEffect, useRef, useState } from "react";
import { api, type JobView } from "../api";
import { navigate, routes } from "../router";

const POLL_MS = 3000;
const MAX_CONCURRENT_POLLS = 5;
const RETENTION_DAYS = 7;
const TERMINAL = new Set([
  "succeeded",
  "succeededwithwarnings",
  "failed",
  "cancelled",
]);

interface State {
  loaded: boolean;
  rows: JobView[];
  nextCursor: string | null;
  error: string | null;
  loadingMore: boolean;
}

export function History() {
  const [state, setState] = useState<State>({
    loaded: false,
    rows: [],
    nextCursor: null,
    error: null,
    loadingMore: false,
  });

  useEffect(() => {
    let cancelled = false;
    api
      .listJobs({ limit: 20 })
      .then((page) => {
        if (cancelled) return;
        setState({
          loaded: true,
          rows: page.jobs,
          nextCursor: page.next_cursor,
          error: null,
          loadingMore: false,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          loaded: true,
          rows: [],
          nextCursor: null,
          error: e instanceof Error ? e.message : String(e),
          loadingMore: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function applyUpdate(updated: JobView) {
    setState((s) => ({
      ...s,
      rows: s.rows.map((r) => (r.job_id === updated.job_id ? updated : r)),
    }));
  }

  async function loadMore() {
    if (!state.nextCursor || state.loadingMore) return;
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      const page = await api.listJobs({ limit: 20, cursor: state.nextCursor });
      setState((s) => ({
        ...s,
        rows: [...s.rows, ...page.jobs],
        nextCursor: page.next_cursor,
        loadingMore: false,
      }));
    } catch (e) {
      setState((s) => ({
        ...s,
        loadingMore: false,
        error: e instanceof Error ? e.message : String(e),
      }));
    }
  }

  if (!state.loaded) {
    return (
      <div className="card">
        <p className="muted">Loading history…</p>
      </div>
    );
  }

  if (state.error && state.rows.length === 0) {
    return (
      <div className="card">
        <div className="error">{state.error}</div>
      </div>
    );
  }

  if (state.rows.length === 0) {
    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>No jobs yet</h2>
        <p className="muted">
          Drop a screen recording to get started — recspec turns it into an
          OpenSpec change you can drop into a repo.
        </p>
        <p style={{ marginTop: 16 }}>
          <button className="primary" onClick={() => navigate(routes.home())}>
            Submit a video
          </button>
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="row" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Your jobs</h2>
        <button className="primary" onClick={() => navigate(routes.home())}>
          Submit a new video
        </button>
      </div>
      <ul className="job-list">
        {state.rows.map((job) => (
          <PollingRow key={job.job_id} job={job} onUpdate={applyUpdate} />
        ))}
      </ul>
      {state.nextCursor && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            className="secondary"
            onClick={loadMore}
            disabled={state.loadingMore}
          >
            {state.loadingMore ? "Loading…" : "Load older"}
          </button>
        </div>
      )}
    </div>
  );
}

// A small fan-out budget so we don't open dozens of polls if the user has
// a flood of in-progress jobs. Rows mark themselves as wanting a poll
// slot via the leasePoll() / releasePoll() pair.
let activePolls = 0;
const pollWaiters: Array<() => void> = [];

function leasePoll(): Promise<void> {
  if (activePolls < MAX_CONCURRENT_POLLS) {
    activePolls += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    pollWaiters.push(() => {
      activePolls += 1;
      resolve();
    });
  });
}
function releasePoll() {
  activePolls = Math.max(0, activePolls - 1);
  const next = pollWaiters.shift();
  if (next) next();
}

function PollingRow({
  job,
  onUpdate,
}: {
  job: JobView;
  onUpdate: (j: JobView) => void;
}) {
  const [current, setCurrent] = useState(job);
  const cancelledRef = useRef(false);

  useEffect(() => {
    setCurrent(job);
  }, [job]);

  useEffect(() => {
    cancelledRef.current = false;
    if (TERMINAL.has(current.status.toLowerCase())) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let leased = false;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      try {
        const fresh = await api.getJob(current.job_id);
        if (stopped) return;
        setCurrent(fresh);
        onUpdate(fresh);
        if (TERMINAL.has(fresh.status.toLowerCase())) return;
        timer = setTimeout(tick, POLL_MS);
      } catch {
        // swallow — row stays on the last good state
        if (!stopped) timer = setTimeout(tick, POLL_MS * 4);
      }
    };

    leasePoll().then(() => {
      if (stopped) {
        releasePoll();
        return;
      }
      leased = true;
      void tick();
    });
    return () => {
      stopped = true;
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
      if (leased) releasePoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.job_id]);

  const expired = isExpired(current);
  const status = current.status.toLowerCase();
  const stage = current.stage_progress?.stage ?? null;

  return (
    <li className="job-row" onClick={() => navigate(routes.job(current.job_id))}>
      <div className="job-row-main">
        <div className="job-row-id">
          <code>{current.job_id.slice(0, 12)}…</code>
          <StatusBadge status={status} />
          {expired && <span className="badge badge-muted">expired</span>}
        </div>
        <div className="muted job-row-meta">
          {formatTimestamp(current.created_at)}
          {current.duration_display ? ` · ${current.duration_display}` : ""}
          {` · ${current.cost_display}`}
          {!TERMINAL.has(status) && stage ? ` · ${stage}` : ""}
        </div>
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "succeeded" || status === "succeededwithwarnings"
      ? "badge badge-ok"
      : status === "failed"
        ? "badge badge-err"
        : status === "cancelled"
          ? "badge badge-muted"
          : "badge badge-active";
  return <span className={cls}>{status}</span>;
}

function isExpired(j: JobView): boolean {
  const created = Date.parse(j.created_at);
  if (Number.isNaN(created)) return false;
  return Date.now() - created > RETENTION_DAYS * 24 * 60 * 60 * 1000;
}

function formatTimestamp(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
